import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Fab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import ResponsiveIframe from "components/ResponsiveIframe";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ResponseOfCertificateTheme } from "services/KnowfullyAdminClient";
import {
  Orientation,
  Severity,
  SnackbarMessageProps,
} from "types/common-types";
import translateError from "utils/translateError";

const styles = {
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  paper: {
    border: "4px solid #F0E4D3",
    borderRadius: "5px",
    margin: "20px",
  },
  container: {
    position: "relative",
    overflow: "hidden",
    width: "100%",
    paddingTop: "100%",
  },
  responsiveIframe: {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    width: "100%",
    height: "100%",
    border: 0,
  },
};

const CertificateThemeDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    certificateService,
    certificateThemesUrl,
    certificateThemeEditUrl,
  } = useGlobalContext();
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const certificateThemeResult = useQuery<ResponseOfCertificateTheme>(
    ["certificateTheme", id],
    () => certificateService.getCertificateTheme(id!),
    {
      enabled: !!id,
    }
  );

  const certificateTheme = certificateThemeResult.data?.data;
  useTitle(certificateTheme?.name || "Certificate Theme Details");

  if (!id) return <Navigate to={certificateThemesUrl()} replace />;
  if (certificateThemeResult.isLoading) return <Loader />;
  if (certificateThemeResult.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="certificateThemeLoadError" />
        </Alert>
      </Box>
    );
  }

  const handleDelete = async () => {
    try {
      await registerPromise(certificateService.deleteCertificateTheme(id));
      queryClient.invalidateQueries("certificateThemes");
      deleteOpenActions.setFalse();
      navigate(certificateThemesUrl());
    } catch (error: any) {
      setSnackbarMessage(
        new SnackbarMessageProps(translateError(error, t), Severity.Error)
      );
    }
  };

  const rows = [
    {
      title: t({ id: "id" }),
      component: certificateTheme?.id && <span>{certificateTheme.id}</span>,
    },
    {
      title: t({ id: "organization" }),
      component: certificateTheme?.organizationName && (
        <span>{certificateTheme.organizationName}</span>
      ),
    },
    {
      title: t({ id: "certificateThemeFormNameLabel" }),
      component: certificateTheme?.name && <span>{certificateTheme.name}</span>,
    },
    {
      title: t({ id: "certificateThemeFormValueLabel" }),
      component: certificateTheme?.value && (
        <span>{certificateTheme.value}</span>
      ),
    },
    {
      title: t({ id: "certificateThemeFormDescriptionLabel" }),
      component: certificateTheme?.description && (
        <span>{certificateTheme.description}</span>
      ),
    },
    {
      title: t({ id: "orientation" }),
      component: certificateTheme && (
        <span>
          {certificateTheme?.isLandscape
            ? Orientation.Landscape
            : Orientation.Portrait}
        </span>
      ),
    },
    {
      title: t({ id: "certificateThemeFormContentLabel" }),
      component: certificateTheme?.themeHtml && (
        <Paper sx={styles.paper}>
          <ResponsiveIframe
            srcDoc={certificateTheme.themeHtml}
            title="themeHtml"
          />
        </Paper>
      ),
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <RedirectBackButton
        redirectRoute={certificateThemesUrl()}
        labelKey="backToListButtonLabel"
      />
      <h2>{certificateTheme?.name}</h2>

      <Box my={3} display="flex" justifyContent="center">
        <Link
          to={certificateThemeEditUrl(id)}
          style={{ textDecoration: "none" }}
        >
          <Fab color="primary" data-cy="editButton">
            <EditIcon />
          </Fab>
        </Link>
        <Box ml={4} />
        <Fab
          color="secondary"
          onClick={deleteOpenActions.setTrue}
          data-cy="deleteButton"
        >
          <DeleteIcon />
        </Fab>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell
                  component="th"
                  scope="row"
                  style={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                >
                  {title}
                </TableCell>
                <TableCell width="100%">{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteEntityDialog
        dialogTitleKey="certificateThemeDeleteTitle"
        contentKey="certificateThemeDeleteInfo"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default CertificateThemeDetails;
